import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getBooking, cancelBooking } from '../../../services/firestore/bookings';
import { getTrip } from '../../../services/firestore/trips';
import { getHostProfile } from '../../../services/firestore/hosts';
import { getUserData } from '../../../services/auth';
import { 
  getOrCreateBookingThread, 
  getThreadMessages, 
  sendMessage 
} from '../../../services/firestore/messages';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Users, 
  Mail, 
  Phone, 
  MessageSquare,
  XCircle,
  Clock,
  DollarSign,
  User,
  Send,
  Shield
} from 'lucide-react';

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [trip, setTrip] = useState(null);
  const [hostProfile, setHostProfile] = useState(null);
  const [hostUser, setHostUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Check if tab query parameter is set
    return searchParams.get('tab') === 'messages' ? 'messages' : 'details';
  });
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId) return;
      try {
        const bookingData = await getBooking(bookingId);
        if (bookingData) {
          setBooking(bookingData);
          
          // Load trip and host data
          const [tripData, profileData, hostData] = await Promise.all([
            getTrip(bookingData.tripId).catch(() => null),
            getHostProfile(bookingData.hostId).catch(() => null),
            getUserData(bookingData.hostId).catch(() => null)
          ]);
          setTrip(tripData);
          setHostProfile(profileData);
          setHostUser(hostData);
          
          // Initialize or get message thread
          if (user && bookingData.clientId === user.uid) {
            const participants = [bookingData.hostId, bookingData.clientId];
            await getOrCreateBookingThread(bookingId, participants);
            loadMessages();
          }
        }
      } catch (error) {
        console.error('Error loading booking:', error);
      } finally {
        setLoading(false);
      }
    };
    loadBooking();
  }, [bookingId, user]);

  const loadMessages = async () => {
    if (!bookingId) return;
    try {
      const threadId = `booking_${bookingId}`;
      const messagesData = await getThreadMessages(threadId);
      console.log('📨 Raw messages data from API:', messagesData);
      console.log('📨 First message sample:', messagesData[0]);
      if (messagesData[0]) {
        console.log('📨 First message timestamp:', {
          value: messagesData[0].timestamp,
          type: typeof messagesData[0].timestamp,
          isNumber: typeof messagesData[0].timestamp === 'number',
          isString: typeof messagesData[0].timestamp === 'string',
          isObject: typeof messagesData[0].timestamp === 'object',
          hasToDate: !!messagesData[0].timestamp?.toDate,
          hasSeconds: messagesData[0].timestamp?.seconds !== undefined,
          fullTimestamp: messagesData[0].timestamp
        });
      }
      setMessages(messagesData);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'messages' && bookingId) {
      loadMessages();
      // Scroll to bottom when messages load
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [activeTab, bookingId]);

  const handleCancel = async () => {
    if (!bookingId) return;
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      await cancelBooking(bookingId);
      setBooking((prev) => prev ? { ...prev, status: 'canceled' } : null);
      alert('Booking canceled successfully!');
    } catch (error) {
      console.error('Error canceling booking:', error);
      alert('Failed to cancel booking. Please try again.');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !bookingId || !user) return;

    setSendingMessage(true);
    try {
      const threadId = `booking_${bookingId}`;
      await sendMessage(threadId, {
        senderId: user.uid,
        senderName: user.displayName || user.email || 'Guest',
        content: messageText.trim(),
      });
      setMessageText('');
      await loadMessages();
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Booking not found</p>
          <button
            onClick={() => navigate(-1)}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const selectedDate = booking.selectedDate 
    ? (booking.selectedDate?.toDate ? booking.selectedDate.toDate() : new Date(booking.selectedDate))
    : null;
  const startDate = trip?.startDate?.toDate ? trip.startDate.toDate() : trip?.startDate ? new Date(trip.startDate) : null;
  const endDate = trip?.endDate?.toDate ? trip.endDate.toDate() : trip?.endDate ? new Date(trip.endDate) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
      <div className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to My Trips</span>
        </button>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'details'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Booking Details
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'messages'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Messages</span>
            </button>
          </nav>
        </div>

        {activeTab === 'details' ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-light text-gray-900 mb-2">Booking Details</h1>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      booking.status === 'confirmed'
                        ? 'bg-emerald-100 text-emerald-800'
                        : booking.status === 'pending'
                        ? 'bg-amber-100 text-amber-800'
                        : booking.status === 'canceled'
                        ? 'bg-red-100 text-red-800'
                        : booking.status === 'completed'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {booking.status}
                  </span>
                  {booking.type === 'inquiry' && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      Inquiry
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Trip Information */}
            {trip && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Trip Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{trip.title}</h3>
                    {trip.location && (
                      <div className="flex items-center gap-2 text-gray-600 mb-2">
                        <MapPin className="w-4 h-4 text-emerald-600" />
                        <span>{trip.location}</span>
                      </div>
                    )}
                    {startDate && endDate && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 text-emerald-600" />
                        <span>
                          {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} -{' '}
                          {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Host Information */}
            {(hostUser || hostProfile) && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Host Information</h2>
                <div className="flex items-center gap-4">
                  {hostUser?.photoURL ? (
                    <img
                      src={hostUser.photoURL}
                      alt={hostUser.displayName || 'Host'}
                      className="w-16 h-16 rounded-full border-2 border-emerald-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white text-xl font-medium border-2 border-emerald-200">
                      {(hostUser?.displayName || hostUser?.email || 'H')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-lg">
                        {hostUser?.displayName || 'Host'}
                      </p>
                      {hostProfile?.verified && (
                        <div className="flex items-center gap-1 text-emerald-600">
                          <Shield className="w-4 h-4" />
                          <span className="text-xs font-medium">Verified</span>
                        </div>
                      )}
                    </div>
                    {hostUser?.email && (
                      <div className="flex items-center gap-2 text-gray-600 mt-1">
                        <Mail className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm">{hostUser.email}</span>
                      </div>
                    )}
                    {hostProfile?.bio && (
                      <p className="text-sm text-gray-600 mt-2">{hostProfile.bio}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Booking Details */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Booking Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedDate && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-500">Arrival Date</p>
                      <p className="font-medium">
                        {selectedDate.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                )}
                {booking.guestCount && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Users className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-500">Number of Guests</p>
                      <p className="font-medium">{booking.guestCount} {booking.guestCount === 1 ? 'guest' : 'guests'}</p>
                    </div>
                  </div>
                )}
                {booking.totalAmount && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className="font-medium text-lg">
                        ${typeof booking.totalAmount === 'number' ? booking.totalAmount.toFixed(2) : booking.totalAmount}
                      </p>
                    </div>
                  </div>
                )}
                {booking.createdAt && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-500">Created</p>
                      <p className="font-medium">
                        {booking.createdAt?.toDate 
                          ? booking.createdAt.toDate().toLocaleDateString('en-US', { 
                              month: 'long', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })
                          : new Date(booking.createdAt).toLocaleDateString('en-US', { 
                              month: 'long', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Your Message */}
            {booking.message && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Your Message</h2>
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{booking.message}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            {booking.status === 'pending' && (
              <div className="border-t border-gray-200 pt-6">
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-6 py-3 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                  Cancel Booking
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 flex flex-col" style={{ height: '600px' }}>
            {/* Messages Header */}
            <div className="border-b border-gray-200 p-4">
              <h2 className="text-xl font-semibold text-gray-900">Messages</h2>
              {hostUser && (
                <p className="text-sm text-gray-600 mt-1">
                  Conversation with {hostUser.displayName || hostUser.email || 'Host'}
                </p>
              )}
            </div>

            {/* Messages List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                </div>
              ) : (
                messages.map((message, index) => {
                  const isOwnMessage = message.senderId === user?.uid;
                  
                  // Debug: Log message payload to see structure
                  console.log(`Message ${index}:`, {
                    id: message.id,
                    timestamp: message.timestamp,
                    timestampType: typeof message.timestamp,
                    timestampValue: message.timestamp,
                    timestampStringified: JSON.stringify(message.timestamp),
                    hasToDate: !!message.timestamp?.toDate,
                    hasSeconds: message.timestamp?.seconds !== undefined,
                    hasToMillis: !!message.timestamp?.toMillis,
                    fullMessage: message
                  });
                  
                  // Handle timestamp from backend API
                  let messageDate;
                  if (!message.timestamp) {
                    // No timestamp - this shouldn't happen, but log it
                    console.warn('Message missing timestamp:', message.id);
                    messageDate = new Date();
                  } else if (typeof message.timestamp === 'number') {
                    // Backend returns timestamp in milliseconds
                    messageDate = new Date(message.timestamp);
                  } else if (message.timestamp?.toDate) {
                    // Firestore Timestamp object (client SDK) - should not happen with API
                    messageDate = message.timestamp.toDate();
                  } else if (message.timestamp?.seconds !== undefined || message.timestamp?._seconds !== undefined) {
                    // Firestore Timestamp object (with or without underscore prefix)
                    const seconds = message.timestamp.seconds ?? message.timestamp._seconds;
                    const nanoseconds = message.timestamp.nanoseconds ?? message.timestamp._nanoseconds ?? 0;
                    messageDate = new Date(seconds * 1000 + nanoseconds / 1000000);
                  } else if (typeof message.timestamp === 'string') {
                    // ISO string or other string format
                    messageDate = new Date(message.timestamp);
                  } else {
                    // Fallback - log warning
                    console.warn('Unexpected timestamp format:', message.timestamp, 'for message:', message.id);
                    messageDate = new Date();
                  }

                  // Validate date
                  if (isNaN(messageDate.getTime())) {
                    console.warn('Invalid date from timestamp:', message.timestamp, 'for message:', message.id);
                    messageDate = new Date();
                  }

                  // Format date intelligently
                  const formatMessageDate = (date) => {
                    const now = new Date();
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);
                    
                    const diffTime = today - messageDay;
                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                    
                    if (diffDays === 0) {
                      // Today - show time only
                      return date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit' 
                      });
                    } else if (diffDays === 1) {
                      // Yesterday - show "Yesterday" and time
                      return `Yesterday ${date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit' 
                      })}`;
                    } else if (diffDays < 7) {
                      // Within a week - show day name and time
                      return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit' 
                      })}`;
                    } else {
                      // Older - show full date and time
                      return date.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                      }) + ' ' + date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit' 
                      });
                    }
                  };

                  // Check if we should show a date separator
                  const prevMessage = index > 0 ? messages[index - 1] : null;
                  let prevMessageDate = null;
                  if (prevMessage?.timestamp) {
                    if (typeof prevMessage.timestamp === 'number') {
                      // Backend returns timestamp in milliseconds
                      prevMessageDate = new Date(prevMessage.timestamp);
                    } else if (prevMessage.timestamp?.toDate) {
                      prevMessageDate = prevMessage.timestamp.toDate();
                    } else if (prevMessage.timestamp?.seconds !== undefined || prevMessage.timestamp?._seconds !== undefined) {
                      // Firestore Timestamp object (with or without underscore prefix)
                      const seconds = prevMessage.timestamp.seconds ?? prevMessage.timestamp._seconds;
                      const nanoseconds = prevMessage.timestamp.nanoseconds ?? prevMessage.timestamp._nanoseconds ?? 0;
                      prevMessageDate = new Date(seconds * 1000 + nanoseconds / 1000000);
                    } else if (typeof prevMessage.timestamp === 'string') {
                      prevMessageDate = new Date(prevMessage.timestamp);
                    }
                  }
                  
                  // Validate previous message date
                  if (prevMessageDate && isNaN(prevMessageDate.getTime())) {
                    prevMessageDate = null;
                  }
                  
                  const showDateSeparator = !prevMessageDate || 
                    new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate()).getTime() !== 
                    new Date(prevMessageDate.getFullYear(), prevMessageDate.getMonth(), prevMessageDate.getDate()).getTime();

                  return (
                    <div key={message.id}>
                      {showDateSeparator && (
                        <div className="flex items-center justify-center my-4">
                          <div className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                            {(() => {
                              const now = new Date();
                              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                              const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
                              const diffTime = today - messageDay;
                              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                              
                              if (diffDays === 0) {
                                return 'Today';
                              } else if (diffDays === 1) {
                                return 'Yesterday';
                              } else {
                                // Always show full date format: "Jan 3 2026"
                                return messageDate.toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                });
                              }
                            })()}
                          </div>
                        </div>
                      )}
                      <div
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                            isOwnMessage
                              ? 'bg-emerald-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm font-medium mb-1">
                            {message.senderName}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          <p className={`text-xs mt-1 ${
                            isOwnMessage ? 'text-emerald-100' : 'text-gray-500'
                          }`}>
                            {formatMessageDate(messageDate)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={sendingMessage}
                />
                <button
                  type="submit"
                  disabled={!messageText.trim() || sendingMessage}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetails;
